import React from "react";
import Tooltip from "apollo-react/components/Tooltip";
import Card from "apollo-react/components/Card";
import Typography from "apollo-react/components/Typography";
import DataAnalyticsIcon from "../Icons/DataAnalyticsIcon";
import BaxterIcon from "../Icons/BaxterIcon";
import LockIcon from "../Icons/LockIcon";
import { useUserAccess } from "../components/AccessControlledApp";
import { appConfig, isDomainAllowed } from "../config/appConfig";

// Extended portal data with API mapping and descriptions for tooltips
const portalData = [
  {
    id: 1,
    title: "CDDP Clinspark",
    subtitle: "IQVIA",
    description: "eSource platform to collect and organise clinical data from connected devices.",
    apiName: "ClinSpark", // Matches API response exactly
    isStaticAccess: false,
    isRestricted: true,
    icon: DataAnalyticsIcon,
    iconColor: "#0557D5",
  },
  {
    id: 2,
    title: "MoveIT",
    subtitle: "IQVIA",
    description: "Secure platform for file sharing and storage.",
    apiName: "MoveIt", // Matches API response exactly
    isRestricted: true,
    isStaticAccess: false,
    icon: DataAnalyticsIcon,
    iconColor: "#0557D5",
  },
  {
    id: 3,
    title: "QBox",
    subtitle: "IQVIA",
    description: "Legacy system for uploading ECG data via USB.",
    apiName: "Qbox", // Matches API response exactly
    isStaticAccess: false,
    icon: DataAnalyticsIcon,
    iconColor: "#0557D5",
  },
  {
    id: 4,
    title: "Cardiac Safety Web Portal",
    subtitle: "IQVIA",
    description: "Manage ECG uploads, clinical reports, query management and safety assessments.",
    apiName: "Cardiacsafety", // Matches API response exactly (lowercase 's')
    isStaticAccess: false,
    icon: DataAnalyticsIcon,
    isRestricted: true,
    iconColor: "#0557D5",
  },
  {
    id: 5,
    title: "Study Management Suite",
    subtitle: "IQVIA",
    description: "Legacy system for device data collection, query management and report generation.",
    apiName: "SMS", // Maps to API response
    isStaticAccess: false,
    icon: DataAnalyticsIcon,
    isRestricted: true,
    iconColor: "#0557D5",
  },
  {
    id: 6,
    title: "IQVIA Connected Devices Training Portal",
    subtitle: "IQVIA",
    description: "Centralized hub for clinical study training resources.",
    apiName: null, // No API mapping - always accessible
    isStaticAccess: true,
    icon: DataAnalyticsIcon,
    iconColor: "#0557D5",
    fallbackUrl: appConfig.APP_TRAINING_PORTAL
  },
  {
    id: 7,
    title: "Holter Web Upload",
    subtitle: "Baxter",
    description: "Upload ECGs from Holter monitors.",
    apiName: "Holter", // Matches API response exactly
    isStaticAccess: false,
    icon: BaxterIcon,
    iconColor: "black",
  },
  {
    id: 8,
    title: "Schiller",
    subtitle: "IQVIA",
    description: "Upload ECGs from Schiller electrocardiographs.",
    apiName: "Schiller", // Matches API response exactly
    isStaticAccess: false,
    icon: DataAnalyticsIcon,
    iconColor: "#0557D5",
  },
];

const AccessControlledPortalCards: React.FC = () => {
  // Use both hooks to see which one has data
  const { accessData, isLoading } = useUserAccess();
  
  // Use the data from whichever hook has it
  const apiData = accessData;
  
  // Determine access status for a given portal
  const getCardAccessStatus = (portal: any) => {
    // Static access cards (always accessible)
    if (portal.isStaticAccess) {
      return {
        hasAccess: true,
        url: portal.fallbackUrl,
        isRestricted: false
      };
    }


    // API-controlled cards - check if apiName exists in API response
    if (portal.apiName && accessData?.access) {
      const appAccess = accessData.access[portal.apiName];
      
      if (appAccess) {
        const hasApiAccess = appAccess.access === "Yes";
        
        return {
          hasAccess: hasApiAccess,
          url: hasApiAccess ? (appAccess.url || portal.fallbackUrl) : null,
          isRestricted: !hasApiAccess,
          restrictionReason: hasApiAccess ? null : "Insufficient permissions"
        };
      } else {
        // API name not found in response
        return {
          hasAccess: false,
          url: null,
          isRestricted: true,
          restrictionReason: "Application not found in permissions"
        };
      }
    }

    // Default to restricted if no mapping
    return {
      hasAccess: false,
      url: null,
      isRestricted: true,
      restrictionReason: "Access not configured"
    };
  };

  const isValidUrl = (url: string): boolean => {
    try {
      const urlObj = new URL(url);
      return isDomainAllowed(urlObj.hostname);
    } catch {
      return false;
    }
  };

  const handleCardClick = (portal: any) => {
    const accessStatus = getCardAccessStatus(portal);
    
    if (accessStatus.hasAccess && accessStatus.url) {
      if (isValidUrl(accessStatus.url)) {
      window.open(accessStatus.url, '_blank', 'noopener,noreferrer');
      } else {
        console.error('Invalid URL detected');
        alert(`The URL for ${portal.title} is not valid or not allowed.`);
      }
    } else {
      const reason = accessStatus.restrictionReason || "Access denied";
      alert(`Access to ${portal.title} is restricted: ${reason}`);
    }
  };

  return (
    <div
      style={{
        backgroundColor: "#F6F7FB",
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      {/* Shared container for alignment */}
      <div
        style={{
          padding: "clamp(16px, 4vw, 24px)",
          maxWidth: "1200px",
          margin: "0 auto",
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        <Typography
          className="fw700-32"
          style={{
            marginBottom: "clamp(16px, 3vw, 24px)",
            textAlign: "left",
          }}
        >
          Applications
        </Typography>

        {/* Show loader while fetching access data */}
        {isLoading && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '300px',
            width: '100%'
          }}>
            <div style={{
              width: '50px',
              height: '50px',
              border: '4px solid #f3f3f3',
              borderTop: '4px solid #0557D5',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }} />
          </div>
        )}

        {!isLoading && (() => {
                   
          const filteredPortals = portalData.filter((portal) => {
            // Always show static access cards (like Holter Web Upload, QBox, Reports Portal)
            if (portal.isStaticAccess) {
              return true;
            }
            
            // For API-controlled cards, check if apiName exists in API response and access is "Yes"
            if (portal.apiName) {
              
              if (apiData?.access) {
                const appAccess = apiData.access[portal.apiName];
                const hasAccess = appAccess?.access === "Yes";
                return hasAccess;
              } else {
                return false;
              }
            }
            
            // Hide cards with no API mapping by default
            return false;
          });
          
          // filteredPortals.forEach(p => console.log(`  ✅ ${p.title}`));

          // Show message if no accessible applications found
          if (!isLoading && accessData && filteredPortals.length === 0) {
            return (
              <div style={{
                textAlign: 'center',
                padding: '40px',
                backgroundColor: '#fff3cd',
                borderRadius: '8px',
                border: '1px solid #ffeaa7',
                margin: '20px 0'
              }}>
                <h4 style={{ color: '#856404', marginBottom: '10px' }}>
                  No Accessible Applications
                </h4>
                <p style={{ color: '#856404', margin: 0 }}>
                  You don't have access to any applications based on your current permissions.
                  Please contact your administrator if you believe this is an error.
                </p>
              </div>
            );
          }

          return (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
                gap: "16px",
                width: "100%",
              }}
            >
              {filteredPortals.map((portal) => {
                const IconComponent = portal.icon;
                const accessStatus = getCardAccessStatus(portal);

            return (
              <div
                key={portal.id}
                style={{
                  display: "flex",
                  width: "100%",
                }}
              >
                <Tooltip title={portal.description} placement="top" enterDelay={300} leaveDelay={100}>
                  <div style={{ width: "100%", display: "flex" }}>
                    <Card
                      interactive={true}
                      onClick={() => handleCardClick(portal)}
                      style={{
                        cursor: accessStatus.hasAccess ? "pointer" : "not-allowed",
                        border: accessStatus.hasAccess ? "1px solid #E0E0E0" : "1px solid #ffcdd2",
                        borderRadius: "12px",
                        padding: "24px",
                        backgroundColor: accessStatus.hasAccess ? "#FFFFFF" : "#fafafa",
                        boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                        transition: "all 0.3s ease",
                        position: "relative",
                        minHeight: "180px",
                        height: "180px",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        width: "100%",
                        boxSizing: "border-box",
                        opacity: accessStatus.hasAccess ? 1 : 0.7,
                      }}
                      onMouseEnter={(e) => {
                        if (accessStatus.hasAccess) {
                          e.currentTarget.style.transform = "translateY(-2px)";
                          e.currentTarget.style.boxShadow = "0 4px 20px rgba(0,0,0,0.15)";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (accessStatus.hasAccess) {
                          e.currentTarget.style.transform = "translateY(0)";
                          e.currentTarget.style.boxShadow = "0 2px 4px rgba(0, 0, 0, 0.1)";
                        }
                      }}
                    >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      width: "100%",
                    }}
                  >
                    <div
                      style={{
                        width: "48px",
                        height: "48px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginBottom: "16px",
                        flexShrink: 0,
                      }}
                    >
                      <IconComponent
                        width={48}
                        height={48}
                        fillColor={accessStatus.hasAccess ? portal.iconColor : "#9e9e9e"}
                      />
                    </div>

                    {portal.isRestricted && (
                      <div
                        style={{
                          padding: "4px 8px",
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          flexShrink: 0,
                          whiteSpace: "nowrap",
                        }}
                      >
                        <LockIcon />
                      </div>
                    )}
                  </div>

                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px",
                      width: "100%",
                      minHeight: "68px",
                    }}
                  >
                    <Typography
                      variant="h6"
                      style={{
                        fontWeight: "600",
                        color: accessStatus.hasAccess ? "#1A1A1A" : "#757575",
                        margin: "0",
                        fontSize: "18px",
                        lineHeight: "1.3",
                        wordBreak: "break-word",
                        minHeight: "47px",
                      }}
                    >
                      {portal.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      style={{
                        color: accessStatus.hasAccess ? "#666666" : "#9e9e9e",
                        margin: "0",
                        fontSize: "14px",
                        lineHeight: "1.4",
                        wordBreak: "break-word",
                      }}
                    >
                      {portal.subtitle}
                    </Typography>
                  </div>
                </Card>
                  </div>
                </Tooltip>
              </div>
            );
          })}
            </div>
          );
        })()}

      </div>

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default AccessControlledPortalCards;
